import { flatten, groupBy } from "ramda";
import { type FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { type IConfiguredPlaybook } from "@/app-utils/playbook.ts";
import { createEditingGridColumnsFromAttributes, getUniqueAttributeId } from "@/app-utils/sample.ts";
import { type Attribute, type ProjectFile } from "@/graphql.ts";
import { type IEditable } from "@/redux/modules/data-editing.ts";
import { getNfNGSFileAttributes } from "@/redux/modules/nf-playbook-execution.ts";
import { getNGSFileAttributeFromWorkflowImportMapping } from "@/redux/modules/playbook-execution.ts";
import {
  type IAttributesTemplatesLookup,
  setSampleGridChanges,
  type TSampleCellValue,
} from "@/redux/modules/sample-editing.ts";
import { type ITableColumn } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ATTRIBUTE_FILE_TYPES, PLAYBOOK_TYPE } from "@/settings/settings.ts";

import EditingGridScreen from "../phenotype/EditingGrid.js";

interface ISampleEditingGridProps {
  attributes: Attribute[];
  templatesLookup: IAttributesTemplatesLookup;
}

/**
 * EditTable for modification of sample data
 */
const SampleEditingGrid: FC<ISampleEditingGridProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const editingGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const isLoading = useSelector<IAppState, boolean>((state) => state.sampleEditingState.isLoading);

  const setEditingGridChanges = useCallback((change) => dispatch(setSampleGridChanges(change)), []);

  const columns: ITableColumn[] = useMemo(
    () => createEditingGridColumnsFromAttributes(props.attributes, props.templatesLookup),
    [props.attributes, props.templatesLookup],
  );

  return (
    <EditingGridScreen
      columns={columns}
      rowData={editingGrid}
      setEditingGridChanges={setEditingGridChanges}
      isLoading={isLoading}
      showLockedColumn={true}
      tableProps={{
        headerComponentParams: {
          sampleEditingMenu: true,
        },
      }}
    />
  );
};

function validateSingleAttributeFiles(
  editingGrid: IEditable[],
  attribute: Attribute,
  isNextflowPlaybook: boolean,
): string {
  const attributeId = getUniqueAttributeId(attribute);
  const cellValues: ProjectFile[][] = editingGrid.map((row) => row[Number(attributeId)]) ?? [];

  // make sure we have the same number of files for every raw, except if the file is "any"
  if (attribute.fileType.id !== ATTRIBUTE_FILE_TYPES.ANY) {
    // check that the file counts are all the same (either one or two for paired end reads case)
    const cellsGroupedByCount = groupBy<ProjectFile[]>((cellValue) => String(cellValue?.length ?? 0), cellValues);
    if (Object.keys(cellsGroupedByCount).length > 1) {
      return "Every sample must either have one raw file or two (for paired-end data). Mixing is not supported.";
    }
  }

  // gather the list of ngs files for additional validation
  const files: ProjectFile[] = flatten(cellValues.map((cellValue: TSampleCellValue) => cellValue as ProjectFile[]));

  // make sure the files are all accessible (might be attached by someone else or historically)
  const restrictedFiles = files.filter((file) => file.hidden);
  if (restrictedFiles.length) {
    return "Some of the files that you provide to the analysis have a restricted access.";
  }

  // Check that files are unique across the experiment (Processor does not support using twice the same file) ;
  // For Playbooks meant for Nextflow  we do not apply this constraint
  if (!isNextflowPlaybook) {
    const filesGroupedByName = groupBy<ProjectFile>(
      (file) => file.fileName!,
      files.filter((file) => file || !file.hidden),
    );
    if (Object.values(filesGroupedByName).find((files: ProjectFile[]) => files.length > 1)) {
      return "Each file that you provide to the analysis must have a unique name.";
    }
  }

  return undefined;
}

export function validateNgsDataFiles(
  playbook: IConfiguredPlaybook,
  attributes: Attribute[],
  editingGrid: IEditable[],
): string {
  const isNextflowPlaybook = playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES;

  let validationMessage: string = undefined;

  if (isNextflowPlaybook) {
    const ngsDataFileAttributes = getNfNGSFileAttributes(playbook, attributes);

    ngsDataFileAttributes.forEach((a) => {
      validationMessage = validateSingleAttributeFiles(editingGrid, a, isNextflowPlaybook);
    });
  } else {
    const ngsDataFileAttribute = getNGSFileAttributeFromWorkflowImportMapping(playbook, attributes);

    validationMessage = validateSingleAttributeFiles(editingGrid, ngsDataFileAttribute, isNextflowPlaybook);
  }

  return validationMessage;
}

export default SampleEditingGrid;
